import React, { useState, useCallback } from 'react';
import Icon, { NAV_SVG } from './icons';
import { tx } from './i18n';

// ── Powloka aplikacji: boczne menu i dolne zakladki (telefon) ────────────────
// Etykiety w NAV_GROUPS maja wiodace emoji ("📒 Log") -- tu pokazujemy sama
// nazwe, a ikone rysujemy jako SVG (icons.js), zeby wygladala tak samo na
// kazdym systemie.

export const stripEmoji = (label) => (label || '').replace(/^\S+\s+/, '');

const readJson = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key)); return v ?? fallback; } catch { return fallback; }
};
const writeJson = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* brak storage */ } };

// Przypiete widoki (per przegladarka) -- sekcja "Przypiete" na gorze menu
export function usePinnedViews() {
  const [pinned, setPinned] = useState(() => readJson('nav_pinned', []));
  const togglePin = useCallback((id) => {
    setPinned(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(-8);
      writeJson('nav_pinned', next);
      return next;
    });
  }, []);
  return [pinned, togglePin];
}

export function NavIcon({ id, size = 18 }) {
  return <Icon name={NAV_SVG[id] ? id : 'chevronRight'} size={size} />;
}

function flatItems(navGroups) {
  const out = {};
  for (const g of navGroups) {
    if (g.type === 'single') out[g.id] = g.label;
    else for (const [id, label] of g.items) out[id] = label;
  }
  return out;
}

export function SidebarNav({ navGroups, view, setView, position, pinned, togglePin, collapsed, setCollapsed }) {
  const activeGroup = navGroups.find(g => g.type === 'group' && g.items.some(([v]) => v === view))?.key;
  const [open, setOpen] = useState(() => {
    const saved = readJson('nav_open_groups', null);
    return new Set(saved || (activeGroup ? [activeGroup] : []));
  });
  const toggleGroup = (key) => setOpen(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    writeJson('nav_open_groups', [...next]);
    return next;
  });
  const labels = flatItems(navGroups);
  const pinnedItems = pinned.filter(id => labels[id]);

  const item = (id, label, sub, keyPrefix = '') => (
    <div key={keyPrefix + id} className={'side-item' + (view === id ? ' side-item--active' : '') + (sub ? ' side-item--sub' : '')}>
      <button type="button" className="side-item__btn" onClick={() => setView(id)} title={collapsed ? stripEmoji(label) : undefined}
        aria-current={view === id ? 'page' : undefined}>
        <NavIcon id={id} />
        <span className="side-item__label">{stripEmoji(label)}</span>
      </button>
      {!collapsed && togglePin && (
        <button type="button" className={'side-item__pin' + (pinned.includes(id) ? ' side-item__pin--on' : '')}
          onClick={() => togglePin(id)} title={pinned.includes(id) ? tx('Odepnij') : tx('Przypnij na górze menu')}
          aria-label={pinned.includes(id) ? tx('Odepnij') : tx('Przypnij')}>
          <Icon name="pinOn" size={14} />
        </button>
      )}
    </div>
  );

  return (
    <nav className={`side-nav side-nav--${position}` + (collapsed ? ' side-nav--collapsed' : '')} aria-label={tx('Menu główne')}>
      <button type="button" className="side-nav__brand" onClick={() => setView('dashboard')} title={tx('Pulpit')}>
        <span className="header__logo-icon"><Icon name="tower" size={19} /></span>
        {!collapsed && <span className="header__title">{tx('HamLogger')}</span>}
      </button>
      <div className="side-nav__scroll">
        {pinnedItems.length > 0 && (
          <div className="side-nav__section">
            {!collapsed && <div className="side-nav__title">{tx('Przypięte')}</div>}
            {pinnedItems.map(id => item(id, labels[id], false, 'pin-'))}
          </div>
        )}
        <div className="side-nav__section">
          {navGroups.map(g => g.type === 'single' ? (
            item(g.id, g.label)
          ) : (
            <div key={g.key} className="side-group">
              <button type="button" className={'side-group__btn' + (g.key === activeGroup ? ' side-group__btn--active' : '')}
                onClick={() => {
                  if (collapsed) { setCollapsed(false); if (!open.has(g.key)) toggleGroup(g.key); }
                  else toggleGroup(g.key);
                }}
                aria-expanded={open.has(g.key)} title={collapsed ? stripEmoji(g.label) : undefined}>
                <Icon name={NAV_SVG[g.key] ? g.key : 'chevronRight'} size={18} />
                <span className="side-item__label">{stripEmoji(g.label)}</span>
                {!collapsed && <Icon name={open.has(g.key) ? 'chevronDown' : 'chevronRight'} size={14} className="side-group__chev" />}
              </button>
              {!collapsed && open.has(g.key) && (
                <div className="side-group__items">
                  {g.items.map(([id, label]) => item(id, label, true))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <button type="button" className="side-nav__collapse" onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? tx('Rozwiń menu') : tx('Zwiń menu do ikon')} aria-label={collapsed ? tx('Rozwiń menu') : tx('Zwiń menu')}>
        <Icon name={(collapsed ? position === 'left' : position !== 'left') ? 'chevronRight' : 'chevronLeft'} size={16} />
        {!collapsed && <span>{tx('Zwiń menu')}</span>}
      </button>
    </nav>
  );
}

// Dolne zakladki na telefonie -- najczestsze widoki pod kciukiem, reszta w "Wiecej"
export function BottomTabs({ view, setView, isOnline, onMore, moreOpen, isSwl, simple }) {
  const tabs = simple ? [
    ...(isSwl ? [['swl', tx('Nasłuchy')]] : [['logger', tx('Logger')]]), ['logbook', tx('Dziennik')],
    ['stats', tx('Statystyki')],
  ] : [
    ...(isSwl ? [['swl', tx('Nasłuchy')]] : [['logger', tx('Logger')]]), ['logbook', tx('Dziennik')],
    ...(isOnline ? [['cluster', tx('Klaster')]] : []),
    ['dashboard', tx('Pulpit')],
  ];
  const inTabs = tabs.some(([id]) => id === view);
  return (
    <nav className="bottom-tabs" aria-label={tx('Szybka nawigacja')}>
      {tabs.map(([id, label]) => (
        <button key={id} type="button" className={'bottom-tabs__btn' + (view === id && !moreOpen ? ' bottom-tabs__btn--active' : '')}
          onClick={() => setView(id)} aria-current={view === id ? 'page' : undefined}>
          <NavIcon id={id} size={22} />
          <span>{label}</span>
        </button>
      ))}
      <button type="button" className={'bottom-tabs__btn' + ((!inTabs || moreOpen) ? ' bottom-tabs__btn--active' : '')} onClick={onMore}
        aria-expanded={moreOpen}>
        <Icon name={moreOpen ? 'x' : 'menu'} size={22} />
        <span>{tx('Więcej')}</span>
      </button>
    </nav>
  );
}
